import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from "./Header";
import parse from "html-react-parser";
import allQuotes from "./quotes.json";
import hallucinatedQuotes from "./hallucinated_quotes.json";
import PropTypes from "prop-types";
import moment from "moment";
import './BenOrBot.css';

const backgroundsCount = 16;
const CLASSIC_QUESTIONS_COUNT = 10;
const SURVIVAL_INITIAL_LIVES = 3;

export const generateQuestions = (count = CLASSIC_QUESTIONS_COUNT) => {
    const usedBenIndices = new Set();
    const usedBotIndices = new Set();
    const questions = [];

    for (let i = 0; i < count; i++) {
        let source = Math.random() < 0.5 ? 'BEN' : 'BOT';

        if (source === 'BEN' && usedBenIndices.size >= allQuotes.length) {
            source = 'BOT';
        } else if (source === 'BOT' && usedBotIndices.size >= hallucinatedQuotes.length) {
            source = 'BEN';
        }

        if (source === 'BEN') {
            const available = [];
            for (let idx = 0; idx < allQuotes.length; idx++) {
                if (!usedBenIndices.has(idx)) available.push(idx);
            }
            if (available.length === 0) break;
            const chosenIdx = available[Math.floor(Math.random() * available.length)];
            usedBenIndices.add(chosenIdx);
            questions.push({
                text: allQuotes[chosenIdx],
                source: 'BEN',
                id: `ben-${chosenIdx}`
            });
        } else {
            const available = [];
            for (let idx = 0; idx < hallucinatedQuotes.length; idx++) {
                if (!usedBotIndices.has(idx)) available.push(idx);
            }
            if (available.length === 0) break;
            const chosenIdx = available[Math.floor(Math.random() * available.length)];
            usedBotIndices.add(chosenIdx);
            questions.push({
                text: hallucinatedQuotes[chosenIdx],
                source: 'BOT',
                id: `bot-${chosenIdx}`
            });
        }
    }

    return questions;
};

export const getNextSurvivalQuestion = (usedBenIndices, usedBotIndices) => {
    const benExhausted = usedBenIndices.size >= allQuotes.length;
    const botExhausted = usedBotIndices.size >= hallucinatedQuotes.length;

    if (benExhausted && botExhausted) {
        return null;
    }

    let source = Math.random() < 0.5 ? 'BEN' : 'BOT';
    if (source === 'BEN' && benExhausted) {
        source = 'BOT';
    } else if (source === 'BOT' && botExhausted) {
        source = 'BEN';
    }

    if (source === 'BEN') {
        const available = [];
        for (let idx = 0; idx < allQuotes.length; idx++) {
            if (!usedBenIndices.has(idx)) available.push(idx);
        }
        if (available.length === 0) return null;
        const chosenIdx = available[Math.floor(Math.random() * available.length)];
        usedBenIndices.add(chosenIdx);
        return {
            text: allQuotes[chosenIdx],
            source: 'BEN',
            id: `ben-${chosenIdx}`
        };
    } else {
        const available = [];
        for (let idx = 0; idx < hallucinatedQuotes.length; idx++) {
            if (!usedBotIndices.has(idx)) available.push(idx);
        }
        if (available.length === 0) return null;
        const chosenIdx = available[Math.floor(Math.random() * available.length)];
        usedBotIndices.add(chosenIdx);
        return {
            text: hallucinatedQuotes[chosenIdx],
            source: 'BOT',
            id: `bot-${chosenIdx}`
        };
    }
};

export const getResultMessage = (score, total = CLASSIC_QUESTIONS_COUNT) => {
    if (score === total) {
        return "Perfection! You know Ben better than Ben knows himself.";
    }
    if (score >= 8) {
        return "Outstanding! You can spot genuine Ben a mile off.";
    }
    if (score >= 5) {
        return "Good effort! But the AI is getting dangerously convincing.";
    }
    if (score >= 3) {
        return "Fooled by the bots! Ben's wisdom is an enigma.";
    }
    return "Total hallucination! Better brush up on your Ben lore.";
};

export const getSurvivalResultMessage = (score) => {
    return `You correctly identified ${score} quote${score === 1 ? '' : 's'} before Ben became indistinguishable from artificial intelligence.`;
};

export default function BenOrBot(props) {
    const [gameMode, setGameMode] = useState('classic'); // 'classic' | 'survival'

    // Classic state
    const [classicQuestions, setClassicQuestions] = useState(() => generateQuestions(CLASSIC_QUESTIONS_COUNT));
    const [classicIndex, setClassicIndex] = useState(0);

    // Survival state
    const usedBenRef = useRef(new Set());
    const usedBotRef = useRef(new Set());
    const [survivalQuestion, setSurvivalQuestion] = useState(() => {
        const benSet = new Set();
        const botSet = new Set();
        const q = getNextSurvivalQuestion(benSet, botSet);
        usedBenRef.current = benSet;
        usedBotRef.current = botSet;
        return q;
    });
    const [lives, setLives] = useState(SURVIVAL_INITIAL_LIVES);
    const [survivalCorpusExhausted, setSurvivalCorpusExhausted] = useState(false);

    // Shared play state
    const [score, setScore] = useState(0);
    const [selectedChoice, setSelectedChoice] = useState(null);
    const [roundFinished, setRoundFinished] = useState(false);
    const [bgIndex, setBgIndex] = useState(() => Math.floor(Math.random() * backgroundsCount));
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Leaderboard state (Survival only)
    const [leaderboard, setLeaderboard] = useState([]);
    const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
    const [leaderboardError, setLeaderboardError] = useState(null);
    const [playerName, setPlayerName] = useState('');
    const [submittingScore, setSubmittingScore] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submittedEntry, setSubmittedEntry] = useState(null);

    useEffect(() => {
        document.title = 'Ben or Bot? - Wheelie Ben';
        document.description = 'Can you distinguish genuine Wheelie Ben quotes from AI hallucinations?';
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchLeaderboard = useCallback(async () => {
        setLoadingLeaderboard(true);
        setLeaderboardError(null);
        try {
            const res = await fetch('/api/leaderboard/');
            if (!res.ok) {
                throw new Error(`HTTP error ${res.status}`);
            }
            const data = await res.json();
            if (data && Array.isArray(data.entries)) {
                setLeaderboard(data.entries);
            } else {
                setLeaderboard([]);
            }
        } catch (err) {
            setLeaderboardError('Unable to load leaderboard.');
        } finally {
            setLoadingLeaderboard(false);
        }
    }, []);

    // Fetch leaderboard when survival round ends
    useEffect(() => {
        if (gameMode === 'survival' && roundFinished) {
            fetchLeaderboard();
        }
    }, [gameMode, roundFinished, fetchLeaderboard]);

    const resetClassic = useCallback(() => {
        setClassicQuestions(generateQuestions(CLASSIC_QUESTIONS_COUNT));
        setClassicIndex(0);
        setSelectedChoice(null);
        setScore(0);
        setRoundFinished(false);
        setBgIndex(prev => (prev + 1) % backgroundsCount);
    }, []);

    const resetSurvival = useCallback(() => {
        const benSet = new Set();
        const botSet = new Set();
        const q = getNextSurvivalQuestion(benSet, botSet);
        usedBenRef.current = benSet;
        usedBotRef.current = botSet;
        setSurvivalQuestion(q);
        setLives(SURVIVAL_INITIAL_LIVES);
        setSurvivalCorpusExhausted(false);
        setSelectedChoice(null);
        setScore(0);
        setRoundFinished(false);
        setPlayerName('');
        setSubmitError(null);
        setSubmittedEntry(null);
        setBgIndex(prev => (prev + 1) % backgroundsCount);
    }, []);

    const handleSwitchMode = (newMode) => {
        if (newMode === gameMode) return;
        setGameMode(newMode);
        if (newMode === 'classic') {
            resetClassic();
        } else {
            resetSurvival();
        }
    };

    const currentQ = gameMode === 'classic' ? classicQuestions[classicIndex] : survivalQuestion;

    const handleChoice = useCallback((choice) => {
        if (selectedChoice !== null || roundFinished || !currentQ) return;

        setSelectedChoice(choice);
        const isAnswerCorrect = choice === currentQ.source;

        if (gameMode === 'classic') {
            if (isAnswerCorrect) {
                setScore(prev => prev + 1);
            }
        } else {
            // Survival mode
            if (isAnswerCorrect) {
                setScore(prev => prev + 1);
            } else {
                setLives(prev => Math.max(0, prev - 1));
            }
        }
    }, [selectedChoice, roundFinished, currentQ, gameMode]);

    const handleNext = useCallback(() => {
        if (gameMode === 'classic') {
            if (classicIndex < classicQuestions.length - 1) {
                setClassicIndex(prev => prev + 1);
                setSelectedChoice(null);
                setBgIndex(prev => (prev + 1) % backgroundsCount);
            } else {
                setRoundFinished(true);
            }
        } else {
            // Survival mode
            if (lives <= 0) {
                setRoundFinished(true);
                return;
            }

            const nextQ = getNextSurvivalQuestion(usedBenRef.current, usedBotRef.current);
            if (!nextQ) {
                setSurvivalCorpusExhausted(true);
                setRoundFinished(true);
            } else {
                setSurvivalQuestion(nextQ);
                setSelectedChoice(null);
                setBgIndex(prev => (prev + 1) % backgroundsCount);
            }
        }
    }, [gameMode, classicIndex, classicQuestions.length, lives]);

    const handleRestart = useCallback(() => {
        if (gameMode === 'classic') {
            resetClassic();
        } else {
            resetSurvival();
        }
    }, [gameMode, resetClassic, resetSurvival]);

    const handleSubmitScore = async (e) => {
        if (e) e.preventDefault();
        const trimmedName = playerName.trim();
        if (!trimmedName) {
            setSubmitError('Please enter a display name.');
            return;
        }
        if (trimmedName.length > 30) {
            setSubmitError('Display name must not exceed 30 characters.');
            return;
        }

        setSubmittingScore(true);
        setSubmitError(null);

        try {
            const res = await fetch('/api/leaderboard/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: trimmedName,
                    score: score
                })
            });

            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || 'Failed to submit score.');
            }

            setSubmittedEntry(data.entry);
            // Refresh leaderboard to show the updated list
            await fetchLeaderboard();
        } catch (err) {
            setSubmitError(err.message || 'Unable to submit score. Please try again.');
        } finally {
            setSubmittingScore(false);
        }
    };

    // Keyboard controls for convenience
    useEffect(() => {
        const handleKeyDown = (e) => {
            // If focused on an input element, do not trigger game shortcuts
            if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
                return;
            }

            if (roundFinished) {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleRestart();
                }
                return;
            }

            if (selectedChoice === null) {
                if (e.key === '1' || e.key === 'b' || e.key === 'B' || e.key === 'ArrowLeft') {
                    handleChoice('BEN');
                } else if (e.key === '2' || e.key === 'o' || e.key === 'O' || e.key === 'ArrowRight') {
                    handleChoice('BOT');
                }
            } else {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
                    handleNext();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [roundFinished, selectedChoice, handleChoice, handleNext, handleRestart]);

    const currentBg = `/images/bg-${(bgIndex % backgroundsCount) + 1}.jpg`;
    const isLongQuote = currentQ ? currentQ.text.length > 110 : false;
    const quoteStyle = {
        fontSize: (isLongQuote && isMobile) ? "2em" : "3em",
    };

    const hasAnswered = selectedChoice !== null;
    const isCorrect = hasAnswered && currentQ && selectedChoice === currentQ.source;

    // Survival hearts renderer
    const renderLives = () => {
        const hearts = [];
        for (let i = 0; i < SURVIVAL_INITIAL_LIVES; i++) {
            const isAlive = i < lives;
            hearts.push(
                <span 
                    key={i} 
                    className={`heart-icon ${isAlive ? 'heart-alive' : 'heart-lost'}`}
                    aria-hidden="true"
                >
                    ♥
                </span>
            );
        }
        return (
            <div className="game-lives-badge" aria-label={`Lives: ${lives} of ${SURVIVAL_INITIAL_LIVES}`}>
                <span className="lives-label">Lives:</span>
                <span className="hearts-container">{hearts}</span>
            </div>
        );
    };

    return (
        <div className="wrap ben-or-bot-wrap">
            <div 
                className="background-blur" 
                style={{ backgroundImage: `url('${currentBg}')` }}
            />
            <div 
                className="background-main" 
                style={{ backgroundImage: `url('${currentBg}')` }}
            />

            <Header navOpen={props.navOpen} toggleNav={props.toggleNav} page="Ben or Bot" />

            <div className="container game-container">
                {/* Mode Selector */}
                <div className="game-mode-selector">
                    <button
                        type="button"
                        className={`mode-tab-btn ${gameMode === 'classic' ? 'active' : ''}`}
                        onClick={() => handleSwitchMode('classic')}
                    >
                        Classic (10 Qs)
                    </button>
                    <button
                        type="button"
                        className={`mode-tab-btn ${gameMode === 'survival' ? 'active' : ''}`}
                        onClick={() => handleSwitchMode('survival')}
                    >
                        Survival (3 Lives)
                    </button>
                </div>

                {!roundFinished && currentQ ? (
                    <>
                        <div className="game-header-info">
                            {gameMode === 'classic' ? (
                                <span className="game-progress-badge">
                                    Question {classicIndex + 1} / {CLASSIC_QUESTIONS_COUNT}
                                </span>
                            ) : (
                                renderLives()
                            )}
                            <span className="game-score-badge">
                                Score: {score}
                            </span>
                        </div>

                        <div className="Quote col-sm-offset-3 col-sm-6 col-md-offset-2 col-md-8 quote game-quote-box" style={quoteStyle}>
                            {parse(currentQ.text)}
                        </div>

                        <div className="game-controls">
                            <div className="game-options-row">
                                <button
                                    type="button"
                                    className={`game-btn-choice ${
                                        hasAnswered
                                            ? currentQ.source === 'BEN'
                                                ? 'correct-reveal'
                                                : selectedChoice === 'BEN'
                                                    ? 'incorrect-selected'
                                                    : 'dimmed'
                                            : ''
                                    }`}
                                    onClick={() => handleChoice('BEN')}
                                    disabled={hasAnswered}
                                >
                                    BEN
                                    <span className="btn-subtext">Genuine Ben</span>
                                </button>

                                <button
                                    type="button"
                                    className={`game-btn-choice ${
                                        hasAnswered
                                            ? currentQ.source === 'BOT'
                                                ? 'correct-reveal'
                                                : selectedChoice === 'BOT'
                                                    ? 'incorrect-selected'
                                                    : 'dimmed'
                                            : ''
                                    }`}
                                    onClick={() => handleChoice('BOT')}
                                    disabled={hasAnswered}
                                >
                                    BOT
                                    <span className="btn-subtext">AI Generated</span>
                                </button>
                            </div>

                            {hasAnswered && (
                                <div className="game-feedback-card">
                                    <div className={`feedback-status ${isCorrect ? 'is-correct' : 'is-incorrect'}`}>
                                        {isCorrect ? 'Correct!' : 'Incorrect!'}
                                    </div>
                                    <div className="feedback-details">
                                        {currentQ.source === 'BEN' 
                                            ? 'This was genuinely said by Ben.' 
                                            : 'This was hallucinated by AI.'}
                                    </div>
                                    <button 
                                        type="button" 
                                        className="game-btn-next" 
                                        onClick={handleNext}
                                        autoFocus
                                    >
                                        {gameMode === 'classic'
                                            ? (classicIndex === classicQuestions.length - 1 ? 'See Final Score' : 'Next Quote')
                                            : (lives <= 0 ? 'See Final Score' : 'Next Quote')
                                        }
                                    </button>
                                </div>
                            )}
                        </div>

                        {gameMode === 'classic' && (
                            <div className="progress-bar-container page-furniture">
                                <div 
                                    className="progress-bar" 
                                    style={{ width: `${((classicIndex + (hasAnswered ? 1 : 0)) / CLASSIC_QUESTIONS_COUNT) * 100}%` }}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="Quote col-sm-offset-3 col-sm-6 col-md-offset-2 col-md-8 quote results-card">
                        {gameMode === 'classic' ? (
                            <>
                                <h2 className="results-title">Round Complete</h2>
                                <div className="results-score-box">
                                    <div className="results-score-label">Your Final Score</div>
                                    <div className="results-score-value">{score} / {CLASSIC_QUESTIONS_COUNT}</div>
                                </div>
                                <p className="results-message">
                                    {getResultMessage(score, CLASSIC_QUESTIONS_COUNT)}
                                </p>
                                <button 
                                    type="button" 
                                    className="game-btn-restart" 
                                    onClick={handleRestart}
                                    autoFocus
                                >
                                    Play Again
                                </button>
                            </>
                        ) : (
                            <>
                                <h2 className="results-title">
                                    {survivalCorpusExhausted ? "SURVIVAL CHAMPION" : "YOU'VE BEEN BOTTED"}
                                </h2>
                                <div className="results-score-box">
                                    <div className="results-score-label">Final Score</div>
                                    <div className="results-score-value">{score}</div>
                                </div>
                                <p className="results-message">
                                    {getSurvivalResultMessage(score)}
                                </p>

                                {/* Leaderboard Submission Form */}
                                <div className="leaderboard-submission-card">
                                    {!submittedEntry ? (
                                        <form onSubmit={handleSubmitScore} className="leaderboard-form">
                                            <label htmlFor="playerNameInput" className="leaderboard-form-label">
                                                Submit your score to the Leaderboard
                                            </label>
                                            <div className="leaderboard-input-row">
                                                <input
                                                    id="playerNameInput"
                                                    type="text"
                                                    className="leaderboard-name-input"
                                                    placeholder="Enter your name"
                                                    maxLength={30}
                                                    value={playerName}
                                                    onChange={(e) => setPlayerName(e.target.value)}
                                                    disabled={submittingScore}
                                                />
                                                <button
                                                    type="submit"
                                                    className="leaderboard-submit-btn"
                                                    disabled={submittingScore}
                                                >
                                                    {submittingScore ? 'Submitting...' : 'Submit Score'}
                                                </button>
                                            </div>
                                            {submitError && (
                                                <div className="leaderboard-error-msg" role="alert">
                                                    {submitError}
                                                </div>
                                            )}
                                        </form>
                                    ) : (
                                        <div className="leaderboard-success-box">
                                            <div className="success-icon">🎉</div>
                                            <div className="success-text">
                                                Score submitted! You ranked <strong>#{submittedEntry.rank}</strong> on the Leaderboard.
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Leaderboard Display */}
                                <div className="leaderboard-display-section">
                                    <h3 className="leaderboard-heading">🏆 Survival Leaderboard (Top 20)</h3>
                                    {loadingLeaderboard ? (
                                        <div className="leaderboard-status-text">Loading leaderboard...</div>
                                    ) : leaderboardError ? (
                                        <div className="leaderboard-status-text error">{leaderboardError}</div>
                                    ) : leaderboard.length === 0 ? (
                                        <div className="leaderboard-status-text">No scores recorded yet. Be the first!</div>
                                    ) : (
                                        <div className="leaderboard-table-wrap">
                                            <table className="leaderboard-table">
                                                <thead>
                                                    <tr>
                                                        <th className="th-rank">#</th>
                                                        <th className="th-name">Name</th>
                                                        <th className="th-score">Score</th>
                                                        <th className="th-date">Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {leaderboard.map((entry, index) => {
                                                        const isUserEntry = submittedEntry && entry.id === submittedEntry.id;
                                                        return (
                                                            <tr key={entry.id || index} className={isUserEntry ? 'user-highlight-row' : ''}>
                                                                <td className="td-rank">{index + 1}</td>
                                                                <td className="td-name">{entry.name}</td>
                                                                <td className="td-score">{entry.score}</td>
                                                                <td className="td-date">
                                                                    {entry.created_at ? moment.utc(entry.created_at).local().format('D MMM YYYY') : '-'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>

                                <button 
                                    type="button" 
                                    className="game-btn-restart" 
                                    onClick={handleRestart}
                                    autoFocus
                                >
                                    Play Again
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

BenOrBot.propTypes = {
    navOpen: PropTypes.bool.isRequired,
    toggleNav: PropTypes.func.isRequired
};
